import { useState, useEffect, useCallback, useRef } from 'react';

interface UseLastParagraphDetectionProps {
  content: string;
  onLastParagraphReached: () => void;
  threshold?: number;
}

export const useLastParagraphDetection = ({
  content,
  onLastParagraphReached,
  threshold = 0.5
}: UseLastParagraphDetectionProps) => {
  const [hasTriggered, setHasTriggered] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !hasTriggered) {
        setHasTriggered(true);
        onLastParagraphReached();
      }
    });
  }, [hasTriggered, onLastParagraphReached]);

  useEffect(() => {
    // Reset trigger state when content changes
    setHasTriggered(false);
  }, [content]);

  useEffect(() => {
    // Add a small delay to ensure DOM is updated
    const timeoutId = setTimeout(() => {
      // Find the last paragraph element with data-last-paragraph attribute
      const lastParagraphElement = document.querySelector('[data-last-paragraph="true"]');
      
      if (!lastParagraphElement) return;

      // Create intersection observer
      observerRef.current = new IntersectionObserver(handleIntersection, {
        threshold,
        rootMargin: '0px 0px -100px 0px' // Trigger slightly before the element is fully visible
      });

      // Start observing
      observerRef.current.observe(lastParagraphElement);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleIntersection, threshold, content]);

  return {
    hasTriggered
  };
}; 